import { forwardRef, useCallback, useMemo } from 'react';
import { BaseSelect, SelectOption } from '../BaseSelect';
import { MultiSelectProps } from './MultiSelect.types';

/**
 * MultiSelect is a dropdown component that allows users to select multiple options.
 * It extends the BaseSelect component and adds specific logic for multi-select behavior.
 */
const MultiSelect = forwardRef<HTMLDivElement, MultiSelectProps>(
  ({ options, searchProps, selected = [], onChange, ...props }, ref) => {
    const filteredOptions = searchProps
      ? options.filter((option) => option.label.toLowerCase().includes(searchProps.searchValue.toLowerCase()))
      : options;

    // Categorizes options into
    // 1. Valid selected options: Selected options that are available.
    // 2. Invalid selected options: Selected values not found in the available options, marked as unrecognized.
    // 3. Unselected options: Available options that are not selected.
    // We add unrecognized options to avoid breaking the UI when the user selects
    // an unavailable option (through URL params for example).
    // De-selecting it will remove it from the list, not allowing it to be selected again.
    const parsedOptions = useMemo(() => {
      const validSelectedOptions = filteredOptions
        .filter((option) => selected.includes(option.value))
        .map((option) => ({ ...option, active: true }));

      // Only show "Unrecognized" options for values that are not in the original options list
      const invalidSelectedOptions: SelectOption[] = selected
        .filter((value) => !options.find((o) => o.value === value))
        .map((value) => ({ label: `${value} (Unrecognized)`, value, active: true }));

      const unselectedOptions = filteredOptions
        .filter((option) => !selected.includes(option.value))
        .map((option, index) => ({ ...option, divider: index === 0 }));

      return [...validSelectedOptions, ...invalidSelectedOptions, ...unselectedOptions];
    }, [filteredOptions, selected]);

    // Handles adding or removing a value from the selected list.
    const handleOptionSelection = useCallback(
      (value?: string) => {
        if (!value) {
          onChange(undefined);
        } else if (selected.includes(value)) {
          // If the new value is already selected, remove it.
          onChange(selected.filter((v) => v !== value));
        } else {
          // Otherwise, add it.
          onChange([...selected, value]);
        }
      },
      [onChange, selected]
    );

    return (
      <BaseSelect
        {...props}
        multiSelect
        ref={ref}
        options={parsedOptions}
        handleOptionSelection={handleOptionSelection}
        searchProps={searchProps}
      />
    );
  }
);

MultiSelect.displayName = 'MultiSelect';

export default MultiSelect;
